// DB-backed book readers. Source of truth for /books, /books/$slug,
// home page BooksSection, and AI guide ecosystem context.
// Public (unauthenticated) endpoints — safe to call from any loader.

import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import type { Book } from "@/lib/content";

type BookRow = Database["public"]["Tables"]["books"]["Row"] & {
  author?: string | null;
  author_legal_name?: string | null;
  category?: string | null;
  tags?: string[] | null;
  cover_alt?: string | null;
  purchase_label?: string | null;
  cover_aspect?: string | null;
  featured?: boolean | null;
  digital_title_slug?: string | null;
};

/** Public Book shape returned to the UI, extended with the optional bridge
 * to a full in-app readable edition (digital_titles). */
export type PublicBook = Book & { digitalTitleSlug?: string };

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

function mapRow(r: BookRow): PublicBook {
  const aspect = r.cover_aspect === "square" ? "square" : "portrait";
  return {
    slug: r.slug,
    title: r.title,
    subtitle: r.subtitle ?? "",
    longDescription: r.long_description ?? "",
    author: r.author ?? undefined,
    authorLegalName: r.author_legal_name ?? undefined,
    category: r.category ?? undefined,
    tags: r.tags ?? undefined,
    coverUrl: r.cover_url ?? undefined,
    coverAlt: r.cover_alt ?? undefined,
    externalPurchaseUrl: r.purchase_url ?? undefined,
    externalPurchaseLabel: r.purchase_label ?? undefined,
    coverAspect: aspect,
    featured: Boolean(r.featured),
    digitalTitleSlug: r.digital_title_slug ?? undefined,
  };
}

export const getBooks = createServerFn({ method: "GET" }).handler(async (): Promise<PublicBook[]> => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("books")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapRow(r as BookRow));
});

export const getBookBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }): Promise<PublicBook | null> => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("books")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row ? mapRow(row as BookRow) : null;
  });
